import Button from 'components/button';
import StyledInput from 'components/styledInput/StyledInput';
import UseContext from 'hooks/useContext';
import { formatMissionCoefficient } from 'helper/missionCoefficient';
import { isSuperAdmin } from 'helper/roles';

const TEXT_ROOT = 'createMission.coefficient';

/**
 * The billing coefficient on the confirmation step: the company value by
 * default, or the one the admin typed for this mission. The field stays open
 * when the company has no value, so that the admin can still set one.
 */
const MissionCoefficientSummary = ({ field }) => {
  const { language, userData } = UseContext();
  const canEdit = isSuperAdmin(userData);
  const text = (key, value) =>
    language
      .getValue(`${TEXT_ROOT}.${key}`, '')
      .split('{value}')
      .join(value ?? '');
  const companyValue = formatMissionCoefficient(field.companyValue);

  const companySummary =
    field.status === 'loading'
      ? text('loading')
      : field.status === 'error'
        ? text('unavailable')
        : companyValue
          ? text('companyValue', companyValue)
          : text('noRange');
  const isOverridden = field.override !== null;

  return (
    <div className="flex flex-col gap-3 py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex justify-between items-center gap-4">
        <span className="ft-sm text-gray-500 shrink-0">{text('title')}</span>
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex flex-col items-end min-w-0">
            <span className="ft-sm text-gray-900 text-right break-words">
              {isOverridden
                ? text('overridden', formatMissionCoefficient(field.override))
                : companySummary}
            </span>
            {isOverridden && companyValue ? (
              <span className="ft-sm text-gray-500 text-right break-words">
                {text('overriddenCompany', companyValue)}
              </span>
            ) : null}
          </div>
          {canEdit && !field.isEditing && (
            <Button size="SM" color="SECONDARY" onClick={field.startEditing}>
              {text('edit')}
            </Button>
          )}
        </div>
      </div>
      {canEdit && field.isEditing && (
        <div className="flex flex-col gap-2">
          <StyledInput
            type="text"
            inputMode="decimal"
            label={text('inputLabel')}
            value={field.input}
            placeholder={companyValue ?? ''}
            error={field.isInvalid ? text('invalid') : undefined}
            hint={field.input.trim() === '' ? text('emptyHint') : undefined}
            onChange={(event) => field.setInput(event.target.value)}
          />
          <div>
            <Button size="SM" color="SECONDARY" onClick={field.reset}>
              {text('reset')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MissionCoefficientSummary;
